import React, { useState } from "react";
import { connect } from 'react-redux';
import Logger, { LOG_LEVELS, ALL_MESSAGES } from "../../utils/logger";
import LanguageStringResolver from "../fields/language/LanguageStringResolver";
import YearSelector from "../selectors/date/YearSelector";
import { SingleCompanySelector } from "../selectors/company/SingleCompanySelector";
import UploadPanel from "../upload/UploadPanel";
import ApplicationMessages from "../inAppMessages/ApplicationMessages";
import getClientConfig from "../../utils/getClientConfig";

const FORM_NAME = 'BULK_ACTIONS_FORM';
const log = new Logger(FORM_NAME, LOG_LEVELS.DEBUG);
const UPLOAD_LIMIT_NUMBER_OF_FILES = 1;
const UPLOAD_LIMIT_FILE_TYPES = [".txt", ".xlsx"];
const MAX_FILE_SIZE_IN_MB = 2;
const INPUT_TYPE_DOCUMENT = 'Document';
const INPUT_TYPE_INVOICE = 'Invoice';

const BulkActionsForm = (props) => {

    const { featureFlags } = getClientConfig();
    const enableRedeliveryAndForceToError = featureFlags?.EnableBulkRedeliveryAndForceToError;

    const [selection, setSelection] = useState({
        inputType: INPUT_TYPE_INVOICE,
        reporterName: props.reporterName,
        year: new Date().getFullYear().toString(),
        ...props.defaultSelections
    });

    // For updating companyCode, year
    const selectionHandler = (keyValue) => {
        log.debug('selectionHandler() - keyValue: ', keyValue);
        let key = Object.keys(keyValue)[0];
        let value = Object.values(keyValue)[0];

        if (key === 'companyCode' && value != 'All') {
            log.clearDictionaryError(ALL_MESSAGES.BLANK_COMPANY);
        }
        else if (key === 'year') {
            log.clearDictionaryError(ALL_MESSAGES.BLANK_TRANSACTION_YEAR);
        }

        let prevSelection = Object.assign({}, selection);
        if (_.isEmpty(value)) {
            delete prevSelection[key];
        } else {
            prevSelection = Object.assign(prevSelection, keyValue);
        }
        setSelection(prevSelection);
    }

    // For updating reason, input type
    const onFieldChangeHandlerFactory = (fieldName) => {
        log.debug('onFieldChangeHandlerFactory(): fieldName: ', fieldName);
        return (event) => {
            let otherFields = {};
            let value = event.target.value;
            if (fieldName === 'inputType') {
                log.clearDictionaryError(ALL_MESSAGES.NO_INPUT_TYPE);
                if (value == INPUT_TYPE_DOCUMENT) {
                    log.clearDictionaryError(ALL_MESSAGES.BLANK_COMPANY);
                    log.clearDictionaryError(ALL_MESSAGES.BLANK_TRANSACTION_YEAR);
                    otherFields = {
                        year: '',
                        companyCode: ''
                    }
                }
                else {
                    otherFields = {
                        year: new Date().getFullYear().toString()
                    }
                }
            }
            else if (fieldName === 'reason') {
                log.clearDictionaryError(ALL_MESSAGES.BLANK_REASON);
            }
            setSelection(prevSelection => ({
                ...prevSelection,
                [fieldName]: value,
                ...otherFields
            }));
        };
    }

    // For adding the file upon upload
    const onUploadRequest = (filesToBeSent) => {
        log.clearDictionaryError(ALL_MESSAGES.NO_FILE);
        setSelection(prevSelection => ({
            ...prevSelection,
            ['file']: filesToBeSent[0]
        }));
    }

    const onSubmitHandler = (event) => {
        log.debug('onSubmitHandler(): ');
        log.clearDictionaryError(ALL_MESSAGES.BLANK_COMPANY, ALL_MESSAGES.NO_INPUT_TYPE, ALL_MESSAGES.BLANK_REASON, ALL_MESSAGES.BLANK_TRANSACTION_YEAR, ALL_MESSAGES.NO_FILE);
        event.preventDefault();


        const company = selection.companyCode && selection.companyCode.trim();
        const inputType = selection.inputType && selection.inputType.trim();
        const reason = selection.reason && selection.reason.trim();
        const year = selection.year && selection.year.trim();
        const file = selection.file;

        let fieldsOk = validateSelections(inputType, reason, file, company, year);

        if (fieldsOk) {
            const action = event?.nativeEvent?.submitter?.name || '';
            props.onSubmit(action, selection);
            let prevSelection = Object.assign({}, selection);
            delete prevSelection['file'];
            setSelection(prevSelection);
        }

        return fieldsOk;
    }

    const validateSelections = (inputType, reason, file, company, year) => {
        let fieldsOk = true;
        if (!inputType || inputType === '') {
            log.setDictionaryMessage(ALL_MESSAGES.NO_INPUT_TYPE);
            fieldsOk = false;
        }
        if (!reason || reason === '') {
            log.setDictionaryMessage(ALL_MESSAGES.BLANK_REASON);
            fieldsOk = false;
        }
        if (!file) {
            log.setDictionaryMessage(ALL_MESSAGES.NO_FILE);
            fieldsOk = false;
        }
        if (inputType === INPUT_TYPE_INVOICE) {
            if (!company || company === '' || company === 'All') {
                log.setDictionaryMessage(ALL_MESSAGES.BLANK_COMPANY);
                fieldsOk = false;
            }
            if (!year || year === '') {
                log.setDictionaryMessage(ALL_MESSAGES.BLANK_TRANSACTION_YEAR);
                fieldsOk = false;
            }
        }
        return fieldsOk;
    }

    return (
        <div>
            <h2 className="mt10 mb5"><LanguageStringResolver string='Title_BulkActionForm_TransactionsDataForm' /></h2>
            <label className='mb15'>
                <LanguageStringResolver string='Label_BulkActionForm_RequiredFields' />
            </label>
            <s-row>
                <s-col span="6">
                    <form className="form" id={FORM_NAME} data-testid='bulk-actions-form' onSubmit={onSubmitHandler}>
                        <ApplicationMessages
                            scopes={[FORM_NAME]}
                            messageType="applicationError"
                            disableDismiss={true}
                        />

                        {/* Input Type Radio Buttons */}
                        <fieldset className="mt10" data-testid='input-type-radio-buttons'>
                            <label>
                                <strong style={{ color: 'red' }}>* </strong>
                                <LanguageStringResolver string='Label_BulkActionForm_InputType' />
                            </label>
                            <input
                                className="radio mt5"
                                type="radio"
                                name="inputType"
                                onChange={onFieldChangeHandlerFactory('inputType')}
                                value={INPUT_TYPE_INVOICE}
                                checked={selection.inputType === INPUT_TYPE_INVOICE}
                                data-testid="invoice-number-radio"
                            />
                            <label className="radio">Invoice Numbers</label>
                            <input
                                className="radio"
                                type="radio"
                                name="inputType"
                                onChange={onFieldChangeHandlerFactory('inputType')}
                                value={INPUT_TYPE_DOCUMENT}
                                checked={selection.inputType === INPUT_TYPE_DOCUMENT}
                                data-testid="document-id-radio"
                            />
                            <label className="radio">Document IDs</label>
                        </fieldset>

                        <fieldset>
                            {/* Company Selector */}
                            <label data-testid='company-selector'>
                                {selection.inputType == INPUT_TYPE_INVOICE ? <strong style={{ color: 'red' }}>* </strong> : <></>}
                                <LanguageStringResolver string='Label_BulkActionForm_Company' />
                            </label>
                            <SingleCompanySelector
                                companyList={props.companyList}
                                fieldName={'companyCode'}
                                selectionHandler={selectionHandler}
                                selectedCompanyId={selection.companyCode}
                                valueCompanyCode={false}
                                extraProps={{ disabled: selection.inputType == INPUT_TYPE_DOCUMENT }}
                            />

                            {/* Year Selector */}
                            <label className="mt10">
                                {selection.inputType == INPUT_TYPE_INVOICE ? <strong style={{ color: 'red' }}>* </strong> : <></>}
                                <LanguageStringResolver string='Label_BulkActionForm_TransactionYear' />
                            </label>
                            <YearSelector
                                year={selection.year}
                                selectionHandler={selectionHandler}
                                data-testid='year-selector'
                                extraProps={{ disabled: selection.inputType == INPUT_TYPE_DOCUMENT }}
                            />

                            {/* Reporter Name Display */}
                            <label className="mt10">
                                <LanguageStringResolver string='Label_BulkActionForm_ReporterName' />
                            </label>
                            <input
                                type='text'
                                value={selection.reporterName ?? ''}
                                className='disabled'
                                disabled={true}
                                data-testid='reporter-name'
                            />

                            {/* Reason For Action Text Box */}
                            <label className="mt10">
                                <strong style={{ color: 'red' }}>* </strong>
                                <LanguageStringResolver string='Label_BulkActionForm_Reason' />
                            </label>
                            <textarea
                                type='text'
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.length <= 500) {
                                        onFieldChangeHandlerFactory('reason')(e);
                                    }
                                }}
                                value={selection.reason ?? ''}
                                data-testid='reason-textbox'
                            />
                            {selection.reason?.length >= 400 && (
                                <span className="absolute r0">
                                    {500 - selection.reason.length} characters remaining
                                </span>
                            )}
                        </fieldset>

                        {/* Displaying File Name upon Upload */}
                        {selection.file != undefined &&
                            <div>
                                <label className="mt10">
                                    <LanguageStringResolver string='Label_BulkActionForm_UploadedFile' />
                                </label>
                                <input
                                    type='text'
                                    value={selection.file.name ?? ''}
                                    className='disabled'
                                    disabled={true}
                                    data-testid='uploaded-file-display'
                                />
                            </div>}
                    </form>
                </s-col>
                <s-col span="6" data-testid='upload-panel'>
                    {/* File Upload Box */}
                    <label>
                        <strong style={{ color: 'red' }}>* </strong>
                        <LanguageStringResolver string='Label_BulkActionForm_Upload' />
                    </label>
                    <UploadPanel
                        uploadLimit={UPLOAD_LIMIT_NUMBER_OF_FILES}
                        acceptTypes={UPLOAD_LIMIT_FILE_TYPES}
                        onUploadRequest={onUploadRequest}
                        maxSizeInKB={MAX_FILE_SIZE_IN_MB * 1000}
                    />
                </s-col>
            </s-row>
            <s-row>
                <s-col span="6">
                    {selection.file != undefined && <label><LanguageStringResolver string="Label_BulkOperationForm_SelectOperationInfo" /></label>}
                    <div className="float-left">
                        <button
                            name="BulkDelete"
                            className={'secondary mt20 mb20'}
                            disabled={selection.file === undefined}
                            form={FORM_NAME}
                            type="submit"
                            data-testid='delete-button'
                        >
                            <LanguageStringResolver string={'Label_BulkActionForm_DeleteButton'} />
                        </button>
                        <button
                            name="BulkForceToError"
                            className={`secondary ${enableRedeliveryAndForceToError ? '' : 'invisible'} mt20 mb20`}
                            disabled={selection.file === undefined}
                            form={FORM_NAME}
                            type="submit"
                            data-testid='force-to-error-button'
                        >
                            <LanguageStringResolver string={'Label_BulkActionForm_ForceToErrorButton'} />
                        </button>
                        <button
                            name="BulkRedeliver"
                            className={`secondary ${enableRedeliveryAndForceToError ? '' : 'invisible'} mt20 mb20`}
                            disabled={selection.file === undefined}
                            form={FORM_NAME}
                            type="submit"
                            data-testid='redeliver-button'
                        >
                            <LanguageStringResolver string={'Label_BulkActionForm_RedeliverButton'} />
                        </button>
                    </div>
                </s-col>
                <s-col span="6">
                    <s-box>
                        <h3><LanguageStringResolver string='downloadBulkOperationTemplateTitle' /></h3>
                        <div className="p10 pt0">
                            <p><LanguageStringResolver string='downloadBulkOperationTemplateText' /></p>
                            <a className="button secondary mb10" href="/bulk-operation-template.xlsx"><LanguageStringResolver string='downloadBulkOperationTemplateBtn' /></a>
                        </div>
                    </s-box>
                </s-col>
            </s-row>
        </div>
    );
};


const mapStateToProps = (state, props) => {
    log.debug('mapStateToProps() - state: ', state);
    log.debug('mapStateToProps() - props: ', props);
    return {
        languages: state.languages || [],
        companyList: state.companies && state.companies?.data || []
    };
}

export default connect(mapStateToProps)(BulkActionsForm);